import type { OptionType } from "$components/inputs";
import { supplierLabel } from "$components/supplier-labels";
import { invoiceLineTotal } from "$routes/_lib/billing-v2/calculate-invoice";
import type { CalculatedInvoice } from "$routes/_lib/billing-v2/invoice-source";
import { PRIVATE_INSURANCE } from "$routes/patient/[:patientId]/_lib/invoices/components/InvoiceCard/InsurerAccordion/formConstants";
import type {
  PriceList,
  PriceListItemRow,
} from "$routes/patient/[:patientId]/_lib/invoices/components/InvoiceCard/types";
import {
  METHOD_NOTE_PREFIX,
  paymentMethodLabel,
} from "$routes/patient/[:patientId]/_lib/invoices-v2/_lib/payment-method-label";
import {
  PayerType,
  type SyncableLineItem,
  type SyncableTransaction,
  TransactionStatus,
  TransactionType,
} from "elephant-web/gql";
import type { InvoiceLineItemRow } from "./invoice-line-item-row";
import {
  type FormatInvoiceMoney,
  type InvoiceMoneyStatus,
  invoiceStatus,
  invoiceTotal,
  outstanding,
  paid,
  patientBalance,
} from "./invoice-money";
import type { InvoiceCoverageEntry } from "./invoice-totals";
import type { InvoicePayerSummary } from "./payers-on-invoice";
import type { CashierLabel, InvoicePaymentEntry } from "./payment-history";
import type { InvoicePayerKind } from "./tokens";

/**
 * The tariff every price list carries for the patient themselves. A line
 * sitting on it is self-pay; a line on any other tariff is, by that fact
 * alone, covered by the insurer that tariff belongs to.
 */
export const SELF_PAY_TARIFF_NAME = "Patient";
export const SELF_PAY_LABEL = "Patient";
const EMPTY_LABEL = "—";
const WAIVED_LABEL = "Waived";
const EXEMPT_LABEL = "Exempt";
const REFUND_LABEL = "Refund";
/** Fallback for transactions written before the method was captured. */
const PAYMENT_LABEL = "Payment";

const SETTLING_TRANSACTION_TYPES: readonly TransactionType[] = [
  TransactionType.Payment,
  TransactionType.Waiver,
  TransactionType.Exempt,
  TransactionType.Refund,
];

export type InvoiceLineView = {
  id: string;
  row: InvoiceLineItemRow;
  /** Only the tariffs this item actually carries, self-pay first. */
  payerOptions: OptionType[];
  tariffId: string | null;
  insurerTariffId: string | null;
  insurerName: string | null;
  unitPriceInPence: number;
  quantity: number;
  amountInPence: number;
};

export type InvoiceInsurerGroup = {
  tariffId: string;
  name: string;
  itemCount: number;
  amountInPence: number;
  claimedLineItems: { lineItemId: string; amountInPence: number }[];
};

export type InvoiceView = {
  lines: InvoiceLineView[];
  insurers: InvoiceInsurerGroup[];
  payers: InvoicePayerSummary[];
  coverage: InvoiceCoverageEntry[];
  itemsBilled: number;
  patientBalance: number;
  paidToDate: number;
  outstanding: number;
  status: InvoiceMoneyStatus;
  payments: InvoicePaymentEntry[];
};

export const priceListItemsById = (
  priceList: PriceList,
): Map<string, PriceListItemRow> =>
  new Map(
    (priceList.priceListItems ?? [])
      .filter((item): item is PriceListItemRow & { uuid: string } =>
        Boolean(item.uuid),
      )
      .map((item) => [item.uuid, item]),
  );

/**
 * Narrower than `OptionType` because the picker searches on the label, so it
 * has to be a plain string.
 */
export type InvoiceItemOption = { value: string; label: string };

export const priceListItemOptions = (
  priceList: PriceList,
): InvoiceItemOption[] =>
  (priceList.priceListItems ?? [])
    .filter((item) => Boolean(item.uuid))
    .map((item) => ({ value: item.uuid as string, label: item.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

const isInsurerTariffName = (name: string | undefined): boolean =>
  Boolean(name) && name !== SELF_PAY_TARIFF_NAME;

/**
 * Private cover is one scheme per patient rather than a named insurer, so the
 * tariff carries the generic name and the scheme's own name is collected on the
 * invoice instead.
 */
export const isPrivateInsurerName = (name: string): boolean =>
  name === PRIVATE_INSURANCE;

/**
 * Picking a tariff is what both prices the line and assigns its payer, so the
 * self-pay tariff leads the list and there is no separate "no insurer" entry.
 */
export const lineItemPayerOptions = (
  item: PriceListItemRow | undefined,
  toMajor: (amountInPence: number) => number,
  formatMoney: FormatInvoiceMoney,
): OptionType[] => {
  const tariffs = (item?.payers ?? []).filter((tariff) => tariff.tariffId);
  const label = (name: string | undefined, amountInPence: number) =>
    `${isInsurerTariffName(name) ? name : SELF_PAY_LABEL} · ${formatMoney(
      toMajor(amountInPence),
    )}`;

  return [
    ...tariffs.filter((tariff) => !isInsurerTariffName(tariff.name)),
    ...tariffs.filter((tariff) => isInsurerTariffName(tariff.name)),
  ].map((tariff) => ({
    value: tariff.tariffId as string,
    label: label(tariff.name, tariff.amountInPence ?? 0),
  }));
};

export const itemTariffForInsurer = (
  item: PriceListItemRow | undefined,
  insurerName: string | null,
): string | null => {
  if (!insurerName) return null;
  return (
    (item?.payers ?? []).find((tariff) => tariff.name === insurerName)
      ?.tariffId ?? null
  );
};

export const itemSelfPayTariff = (
  item: PriceListItemRow | undefined,
): string | null =>
  (item?.payers ?? []).find((tariff) => !isInsurerTariffName(tariff.name))
    ?.tariffId ?? null;

/**
 * Ties are broken on the tariff id so the pick never depends on the order the
 * catalogue happened to return its tariffs in.
 */
const firstInsurerTariffByName = (
  item: PriceListItemRow | undefined,
): string | null =>
  (item?.payers ?? [])
    .flatMap((tariff) =>
      tariff.tariffId && isInsurerTariffName(tariff.name)
        ? [
            {
              tariffId: tariff.tariffId,
              name: (tariff.name ?? "").toLowerCase(),
            },
          ]
        : [],
    )
    .sort(
      (a, b) =>
        a.name.localeCompare(b.name) || a.tariffId.localeCompare(b.tariffId),
    )[0]?.tariffId ?? null;

/**
 * Not every item carries a patient tariff, so stopping at that fallback would
 * leave the line on a tariff the item does not offer and the payer select
 * matching no option — hence the alphabetical insurer behind it.
 */
export const tariffForChosenItem = (
  item: PriceListItemRow | undefined,
  insurerName: string | null,
): string | null =>
  itemTariffForInsurer(item, insurerName) ??
  itemSelfPayTariff(item) ??
  firstInsurerTariffByName(item);

export const tariffUnitPriceInPence = (
  item: PriceListItemRow | undefined,
  tariffId: string | null,
): number | null => {
  if (!tariffId) return null;
  return (
    (item?.payers ?? []).find((tariff) => tariff.tariffId === tariffId)
      ?.amountInPence ?? null
  );
};

export const tariffRevenueDepartmentId = (
  item: PriceListItemRow | undefined,
  tariffId: string | null,
): string | null => {
  const match = (item?.payers ?? []).find(
    (tariff) => tariff.tariffId === tariffId,
  );
  return (
    match?.revenueDepartmentId ?? item?.payers?.[0]?.revenueDepartmentId ?? null
  );
};

export type BuildInvoiceViewArgs = {
  invoice: CalculatedInvoice;
  priceList: PriceList;
  departmentNameById: ReadonlyMap<string, string>;
  membershipByInsurerName: ReadonlyMap<string, string>;
  fallbackItemDate: string;
  /** Pence → major units: the single unit boundary on the read path. */
  toMajor: (amountInPence: number) => number;
  formatMoney: FormatInvoiceMoney;
  formatItemDate: (date: Date) => string;
  formatTimestamp: (date: Date) => string;
  /** Absent on a draft, which has no transactions to attribute. */
  resolveCashier?: (
    userId: string | null | undefined,
  ) => CashierLabel | undefined;
};

const buildLine = (
  lineItem: SyncableLineItem,
  args: BuildInvoiceViewArgs,
  itemsById: Map<string, PriceListItemRow>,
  insurerNameByLineId: ReadonlyMap<string, string>,
): InvoiceLineView => {
  const item = lineItem.priceListItemId
    ? itemsById.get(lineItem.priceListItemId)
    : undefined;
  const tariff = (item?.payers ?? []).find(
    (candidate) => candidate.tariffId === lineItem.tariffId,
  );
  const insurerName = insurerNameByLineId.get(lineItem.id) ?? null;
  const payerKind: InvoicePayerKind = insurerName ? "insurer" : "self-pay";

  const quantity = lineItem.quantity as number;
  const unitPriceInPence = lineItem.cost;
  const amountInPence = invoiceLineTotal({
    cost: unitPriceInPence,
    quantity,
    disabled: lineItem.disabled,
  });
  const incurredAt = lineItem.incurredAt ?? args.fallbackItemDate;

  return {
    id: lineItem.id,
    tariffId: lineItem.tariffId ?? null,
    insurerTariffId: insurerName ? (lineItem.tariffId as string) : null,
    insurerName,
    unitPriceInPence,
    quantity,
    amountInPence,
    payerOptions: lineItemPayerOptions(item, args.toMajor, args.formatMoney),
    row: {
      id: lineItem.id,
      name: item?.name ?? EMPTY_LABEL,
      dateLabel: incurredAt
        ? args.formatItemDate(new Date(incurredAt))
        : undefined,
      departmentLabel: lineItem.revenueDepartmentId
        ? (args.departmentNameById.get(lineItem.revenueDepartmentId) ??
          tariff?.revenueDepartment ??
          EMPTY_LABEL)
        : (tariff?.revenueDepartment ?? EMPTY_LABEL),
      supplierLabel: lineItem.supplier
        ? supplierLabel(lineItem.supplier)
        : EMPTY_LABEL,
      quantityLabel: String(quantity),
      unitPriceLabel: args.formatMoney(args.toMajor(unitPriceInPence)),
      amountLabel: args.formatMoney(args.toMajor(amountInPence)),
      payerLabel: insurerName ?? SELF_PAY_LABEL,
      payerKind,
    },
  };
};

const settlementLabel = (transaction: SyncableTransaction): string => {
  if (transaction.type === TransactionType.Waiver) return WAIVED_LABEL;
  if (transaction.type === TransactionType.Exempt) return EXEMPT_LABEL;
  if (transaction.type === TransactionType.Refund) return REFUND_LABEL;
  return paymentMethodLabel(transaction) ?? PAYMENT_LABEL;
};

/**
 * The waive and exempt reason, which those flows write to `notes` unprefixed.
 * A payment's `notes` carries the method rather than anything the meta line
 * should repeat.
 */
const settlementReference = (
  transaction: SyncableTransaction,
): string | undefined => {
  const notes = transaction.notes ?? undefined;
  return notes?.startsWith(METHOD_NOTE_PREFIX) ? undefined : notes;
};

const buildPaymentEntries = (
  args: BuildInvoiceViewArgs,
  insurers: InvoiceInsurerGroup[],
): InvoicePaymentEntry[] => {
  const successful = args.invoice.transactions.filter(
    (transaction) => transaction.status === TransactionStatus.Success,
  );

  const chargeInsurer = successful.filter(
    (transaction) => transaction.type === TransactionType.ChargeInsurer,
  );

  // Coverage lands on the invoice at finalise, so the booked CHARGE_INSURER
  // transactions are the truthful record. Before they exist (a draft, or an
  // invoice finalised by an older build) the derived payers stand in.
  const coverageEntries: InvoicePaymentEntry[] =
    chargeInsurer.length > 0
      ? chargeInsurer.map((transaction, index) => ({
          kind: "insurer-coverage",
          id: transaction.id ?? `coverage-${index}`,
          insurerName: transaction.payerName ?? EMPTY_LABEL,
          amount: args.toMajor(transaction.amount?.value ?? 0),
          recordedAt: new Date(transaction.timestamp),
          timestampLabel: args.formatTimestamp(new Date(transaction.timestamp)),
          membershipNumber: args.membershipByInsurerName.get(
            transaction.payerName ?? "",
          ),
        }))
      : args.invoice.finalizedAt
        ? insurers.map((insurer) => ({
            kind: "insurer-coverage",
            id: insurer.tariffId,
            insurerName: insurer.name,
            amount: args.toMajor(insurer.amountInPence),
            recordedAt: new Date(args.invoice.finalizedAt as string),
            timestampLabel: args.formatTimestamp(
              new Date(args.invoice.finalizedAt as string),
            ),
            membershipNumber: args.membershipByInsurerName.get(insurer.name),
          }))
        : [];

  const patientEntries: InvoicePaymentEntry[] = successful
    .filter((transaction) =>
      SETTLING_TRANSACTION_TYPES.includes(transaction.type),
    )
    .map((transaction, index) => ({
      kind: "patient-payment",
      id: transaction.id ?? `payment-${index}`,
      methodLabel: settlementLabel(transaction),
      amount: args.toMajor(transaction.amount?.value ?? 0),
      recordedAt: new Date(transaction.timestamp),
      timestampLabel: args.formatTimestamp(new Date(transaction.timestamp)),
      reference: settlementReference(transaction),
      cashier: args.resolveCashier?.(transaction.actionedByUserId),
    }));

  return [...coverageEntries, ...patientEntries];
};

/**
 * The payer of a line is read off its tariff, so the payer list, the coverage
 * ledger and the row accents all follow from the same fact rather than from a
 * separately stored insurer roster.
 */
export const buildInvoiceView = (args: BuildInvoiceViewArgs): InvoiceView => {
  const itemsById = priceListItemsById(args.priceList);
  const insurerPayers = args.invoice.payers.filter(
    (payer) => payer.type === PayerType.Insurer,
  );
  const insurerNameByLineId = new Map(
    insurerPayers.flatMap((payer) =>
      payer.claimedLineItems.map((claim) => [claim.lineItemId, payer.name]),
    ),
  );
  const lines = args.invoice.lineItems
    .filter((lineItem) => !lineItem.disabled)
    .map((lineItem) =>
      buildLine(lineItem, args, itemsById, insurerNameByLineId),
    );
  const insurers = insurerPayers.map<InvoiceInsurerGroup>((payer) => {
    const activeClaims = payer.claimedLineItems.filter((claim) =>
      lines.some((line) => line.id === claim.lineItemId),
    );
    const tariffId =
      activeClaims
        .map((claim) =>
          args.invoice.lineItems.find((line) => line.id === claim.lineItemId),
        )
        .find((line) => line?.tariffId)?.tariffId ??
      payer.id ??
      payer.name;
    return {
      tariffId,
      name: payer.name,
      itemCount: activeClaims.length,
      amountInPence: payer.balance.total,
      claimedLineItems: payer.claimedLineItems,
    };
  });

  return {
    lines,
    insurers,
    payers: [
      ...insurers.map<InvoicePayerSummary>((insurer) => ({
        kind: "insurer",
        id: insurer.tariffId,
        name: insurer.name,
        isPrivate: isPrivateInsurerName(insurer.name),
        itemCount: insurer.itemCount,
        amount: args.toMajor(insurer.amountInPence),
        membershipNumber: args.membershipByInsurerName.get(insurer.name),
      })),
      {
        kind: "self-pay",
        itemCount: lines.filter((line) => !line.insurerName).length,
        amount: args.toMajor(patientBalance(args.invoice)),
      },
    ],
    coverage: insurers.map<InvoiceCoverageEntry>((insurer) => ({
      id: insurer.tariffId,
      insurerName: insurer.name,
      amount: args.toMajor(insurer.amountInPence),
    })),
    itemsBilled: args.toMajor(invoiceTotal(args.invoice)),
    patientBalance: args.toMajor(patientBalance(args.invoice)),
    paidToDate: args.toMajor(paid(args.invoice)),
    outstanding: args.toMajor(outstanding(args.invoice)),
    status: invoiceStatus(args.invoice),
    payments: buildPaymentEntries(args, insurers),
  };
};
